import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withArraySearchFilter,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentArraySearchFilter,
    withMetadata,
    withSearchFilter,
    withSearchFilterArrayUnTyped,
    withSearchFilterUnTyped,
} from '@pf/shared-utility';
import {
    ContainerCreate,
    ContainerDto,
    ContainerRead,
    ContainerSearchParams,
} from '../../entities/container.dto';
import {
    CustomFieldMetadataMap,
    DefaultLocationFragmentMetadataMap,
    ITypeEntityFragmentSymbol,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.Container);

withDefaultEntityMetadata<Omit<ContainerDto, 'children'>>(symbols.dto, {
    parentContainerId: String,
    rootContainerId: String,
    palletPositions: Number,
    resourceAuth: ResourceAuthSymbol,
    lpn: String,
    status: String,
    slot: Object,
    containerType: ITypeEntityFragmentSymbol,
    reservedForLocation: DefaultLocationFragmentMetadataMap.symbols.dto,
    warehouse: Object,
    equipment: Object,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    inventoryBundles: [Object],
});

withDefaultEntityMetadata<ContainerRead>(symbols.entity, {
    parentContainerId: String,
    rootContainerId: String,
    palletPositions: Number,
    resourceAuth: ResourceAuthSymbol,
    lpn: String,
    status: String,
    slot: Object,
    containerType: ITypeEntityFragmentSymbol,
    reservedForLocation: DefaultLocationFragmentMetadataMap.symbols.entity,
    warehouse: Object,
    equipment: Object,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    inventoryBundles: [Object],
});

withMetadata<ContainerCreate>(symbols.createBody, {
    parentContainerId: String,
    rootContainerId: String,
    palletPositions: Number,
    resourceAuth: ResourceAuthSymbol,
    lpn: String,
    status: String,
    slotId: String,
    containerTypeId: String,
    reservedForLocationId: String,
    equipmentId: String,
    warehouseId: String,
});

withDefaultSearchParamsMetadata<ContainerSearchParams>(symbols.searchParams, {
    status: [String],
    lPN: String,
    containerTypeId: [String],
    containerTypeName: [String],
    parentContainerId: String,
    zoneId: [String],
    slotId: [String],
    locationId: String,
    warehouseId: String,
    equipmentIds: [String],
    equipmentTypeIds: [String],
    hasEquipment: Boolean,
    hasParent: Boolean,
});

export const ContainerMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<ContainerRead, ContainerDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<ContainerDto, ContainerRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<ContainerDto, ContainerCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                c => c.equipmentId,
                mapFrom(s => s.equipment?.id || null)
            )
        );
        createMap<IQueryParams<ContainerDto>, ContainerSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('rootContainerId'),
            withSearchFilter('parentContainerId'),
            forMember(
                d => d.lPN,
                mapFrom(s => {
                    return s.filters?.lpn;
                })
            ),
            withSearchFilterArrayUnTyped('zoneId'),
            withWarehouseSearchFilter(),
            withArraySearchFilter('status'),
            withSearchFilterArrayUnTyped('equipmentIds'),
            withSearchFilterArrayUnTyped('equipmentTypeIds'),
            withSearchFilterUnTyped('hasEquipment'),
            withSearchFilterUnTyped('hasParent'),
            withFragmentArraySearchFilter('containerType'),
            withFragmentArraySearchFilter('slot'),
            withSearchFilterArrayUnTyped('slotId'),
            ...withDefaultSearchParamsMappingConfiguration()
        );

        CustomFieldMetadataMap.profile(mapper);
        DefaultLocationFragmentMetadataMap.profile(mapper);
    },
};
